import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { FiArrowLeft, FiBook, FiPlay, FiLock } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL } from '../config/config';

const MyCourses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Redirect to home if not logged in
    if (!user) {
      navigate('/', { replace: true });
      return;
    }
    
    if (user && user.email) {
      fetchPurchasedCourses();
    }
  }, [user, navigate]);

  const fetchPurchasedCourses = async () => {
    try {
      const response = await quizAPI.getMyPurchasedCourses(user.email);
      if (response.data.success) {
        setCourses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-0">
      {/* Header - Mobile Optimized */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <FiBook className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">My Purchase</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="card text-center py-12 max-w-md mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiBook className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No Courses Yet</h3>
            <p className="text-gray-600 mb-6">You haven't purchased any courses</p>
            <Link to="/courses" className="btn-primary inline-block px-8 py-3">
              Browse Courses
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm mb-1">Total Courses</p>
                  <p className="text-3xl font-bold">{courses.length}</p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <FiBook className="w-8 h-8" />
                </div>
              </div>
            </div>

            {/* Courses Grid - Better Mobile Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100 hover:border-primary-200"
                  onClick={() => navigate(`/course/${course.id}`, { state: { course } })}
                >
                  {/* Course Thumbnail */}
                  <div className="relative">
                    {course.course_thumbnail ? (
                      <img
                        src={`${BASE_URL}/${course.course_thumbnail}`}
                        alt={course.title}
                        className="w-full h-40 sm:h-48 object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%236366f1" width="200" height="200"/%3E%3C/svg%3E';
                        }}
                      />
                    ) : (
                      <div className="w-full h-40 sm:h-48 bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                        <FiBook className="w-16 h-16 text-white opacity-90" />
                      </div>
                    )}
                    {/* Purchased Badge */}
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Owned
                    </div>
                  </div>

                  {/* Course Info */}
                  <div className="p-4">
                    <h3 className="text-base font-bold text-gray-800 mb-2 line-clamp-2 min-h-[3rem]">
                      {course.title}
                    </h3>
                    
                    <div className="flex items-center justify-between text-xs mb-4 pb-3 border-b border-gray-100">
                      <span className="flex items-center text-gray-600">
                        <FiBook className="mr-1 text-primary-500 w-3 h-3" />
                        {course.tradegroupName || 'General'}
                      </span>
                      {course.validity && (
                        <span className="text-green-600 font-bold bg-green-50 px-2 py-1 rounded">
                          {course.validity} Months
                        </span>
                      )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/course/${course.id}`, { state: { course } });
                      }}
                      className="btn-primary w-full flex items-center justify-center py-2.5 text-sm font-semibold rounded-lg"
                    >
                      <FiPlay className="mr-2 w-4 h-4" />
                      View Content
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyCourses;